import React, { useState, useEffect } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { InputNumber } from 'primereact/inputnumber';
import moment from 'moment';

const DAY_OPTIONS = [
    { label: 'Monday', value: 'MONDAY' },
    { label: 'Tuesday', value: 'TUESDAY' },
    { label: 'Wednesday', value: 'WEDNESDAY' },
    { label: 'Thursday', value: 'THURSDAY' },
    { label: 'Friday', value: 'FRIDAY' },
    { label: 'Saturday', value: 'SATURDAY' },
    { label: 'Sunday', value: 'SUNDAY' }
];

const TIMEZONE_OPTIONS = [
    { label: 'India Standard Time (IST)', value: 'Asia/Kolkata' },
    { label: 'US Eastern Time (EST)', value: 'America/New_York' },
    { label: 'US Pacific Time (PST)', value: 'America/Los_Angeles' },
    { label: 'UK Time (GMT)', value: 'Europe/London' },
    { label: 'Singapore Time (SGT)', value: 'Asia/Singapore' },
    { label: 'Dubai Time (GST)', value: 'Asia/Dubai' }
];

/**
 * Schedule Configuration Component
 * Renders different schedule configuration forms based on delivery mode
 */
const ScheduleConfiguration = ({ delivery, schedule, onChange, disabled = false, workflowType = null }) => {
    const defaultSchedule = {
        time: null,
        dayOfWeek: null,
        date: null,
        startDate: null,
        timezone: 'Asia/Kolkata',
        daysAheadToCheck: 7
    };
    
    const [config, setConfig] = useState(schedule ? {
        ...defaultSchedule,
        ...schedule,
        timezone: schedule.timezone || 'Asia/Kolkata'
    } : defaultSchedule);

    const [errors, setErrors] = useState({});

    useEffect(() => {
        const defaultSchedule = {
            time: null,
            dayOfWeek: null,
            date: null,
            startDate: null,
            timezone: 'Asia/Kolkata',
            daysAheadToCheck: 7
        };
        
        setConfig(schedule ? {
            ...defaultSchedule,
            ...schedule,
            timezone: schedule.timezone || 'Asia/Kolkata'
        } : defaultSchedule);
        setErrors({});
    }, [delivery, schedule]);

    const handleChange = (field, value) => {
        const updated = { ...config, [field]: value };
        setConfig(updated);
        
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
        
        // Validate and propagate changes
        const validation = validateConfig(updated, delivery);
        if (validation.isValid) {
            // Clean up the schedule object based on delivery mode before sending to parent
            const cleanedSchedule = cleanScheduleForDeliveryMode(updated, delivery);
            onChange(cleanedSchedule);
        }
    };

    /**
     * Remove irrelevant fields based on delivery mode
     */
    const cleanScheduleForDeliveryMode = (schedule, mode) => {
        const cleaned = { ...schedule };
        
        switch (mode) {
            case 'DAILY':
                // Only keep: time, startDate (optional), timezone, daysAheadToCheck
                delete cleaned.date;
                delete cleaned.dayOfWeek;
                break;
            
            case 'WEEKLY':
                // Only keep: time, dayOfWeek, startDate (optional), timezone, daysAheadToCheck
                delete cleaned.date;
                break;
            
            case 'SCHEDULE_ONCE':
                // Only keep: date, time, timezone
                delete cleaned.startDate;
                delete cleaned.dayOfWeek;
                delete cleaned.daysAheadToCheck;
                break;
        }
        
        return cleaned;
    };

    const validateConfig = (cfg, mode) => {
        const newErrors = {};
        
        switch (mode) {
            case 'DAILY':
                if (!cfg.time) {
                    newErrors.time = 'Please select a time';
                }
                // Validate start date if provided (must be in future)
                if (cfg.startDate) {
                    const startDateTime = new Date(cfg.startDate);
                    startDateTime.setHours(0, 0, 0, 0);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    if (startDateTime < today) {
                        newErrors.startDate = 'Start date must be today or in the future';
                    }
                }
                break;
            
            case 'WEEKLY':
                if (!cfg.dayOfWeek) {
                    newErrors.dayOfWeek = 'Please select a day';
                }
                if (!cfg.time) {
                    newErrors.time = 'Please select a time';
                }
                // Validate start date if provided (must be in future)
                if (cfg.startDate) {
                    const startDateTime = new Date(cfg.startDate);
                    startDateTime.setHours(0, 0, 0, 0);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    if (startDateTime < today) {
                        newErrors.startDate = 'Start date must be today or in the future';
                    }
                }
                break;
            
            case 'SCHEDULE_ONCE':
                if (!cfg.date) {
                    newErrors.date = 'Please select a date';
                }
                if (!cfg.time) {
                    newErrors.time = 'Please select a time';
                }
                // Validate future date
                if (cfg.date && cfg.time) {
                    const selectedDateTime = new Date(cfg.date);
                    const [hours, minutes] = parseTime(cfg.time);
                    selectedDateTime.setHours(hours, minutes, 0, 0);
                    
                    if (selectedDateTime <= new Date()) {
                        newErrors.date = 'Selected date/time must be in the future';
                    }
                }
                break;
        }
        
        setErrors(newErrors);
        return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
    };

    const parseTime = (timeValue) => {
        if (!timeValue) return [0, 0];
        
        if (timeValue instanceof Date) {
            return [timeValue.getHours(), timeValue.getMinutes()];
        }
        
        // Handle "HH:MM" or "HH:MM AM/PM" format
        const parts = timeValue.toString().split(' ');
        const time = parts[0];
        const period = parts[1];
        
        let [hours, minutes] = time.split(':').map(Number);
        
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        
        return [hours, minutes];
    };

    const formatTime = (date) => {
        if (!date) return null;
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
    };

    const formatDate = (date) => {
        if (!date) return null;
        // Use local date to avoid timezone conversion issues
        const localDate = new Date(date);
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, '0');
        const day = String(localDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatFullDateTime = (cfg) => {
        if (!cfg.date || !cfg.time) return 'Not configured';
        
        const dateStr = moment(cfg.date).format('MMMM D, YYYY');
        const timeStr = typeof cfg.time === 'string' ? cfg.time : formatTime(cfg.time);
        const tz = cfg.timezone === 'Asia/Kolkata' ? 'IST' : cfg.timezone;
        
        return `${dateStr} at ${timeStr} ${tz}`;
    };

    const getInfoMessage = () => {
        const tz = config.timezone === 'Asia/Kolkata' ? 'IST' : config.timezone;
        
        switch (delivery) {
            case 'DAILY':
                if (!config.time) return 'Please configure the daily schedule time';
                const dailyTimeStr = typeof config.time === 'string' ? config.time : formatTime(config.time);
                if (config.startDate) {
                    const startDateStr = moment(config.startDate).format('MMMM D, YYYY');
                    return `Starting from ${startDateStr}, notifications will be sent every day at ${dailyTimeStr} ${tz}`;
                }
                return `Notifications will be sent every day at ${dailyTimeStr} ${tz}`;
            
            case 'WEEKLY':
                if (!config.dayOfWeek || !config.time) return 'Please configure the weekly schedule';
                const weeklyTimeStr = typeof config.time === 'string' ? config.time : formatTime(config.time);
                if (config.startDate) {
                    const startDateStr = moment(config.startDate).format('MMMM D, YYYY');
                    return `Starting from ${startDateStr}, notifications will be sent every ${config.dayOfWeek.toLowerCase()} at ${weeklyTimeStr} ${tz}`;
                }
                return `Notifications will be sent every ${config.dayOfWeek.toLowerCase()} at ${weeklyTimeStr} ${tz}`;
            
            case 'SCHEDULE_ONCE':
                if (!config.date || !config.time) return 'Please configure the schedule date and time';
                return `Notification will be sent ONCE on ${formatFullDateTime(config)}`;
            
            default:
                return '';
        }
    };

    const parseTimeToDate = (timeStr) => {
        if (!timeStr) return null;
        if (timeStr instanceof Date) return timeStr;
        
        const [hours, minutes] = parseTime(timeStr);
        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        return date;
    };

    const parseDateString = (dateStr) => {
        if (!dateStr) return null;
        if (dateStr instanceof Date) return dateStr;
        return new Date(dateStr);
    };

    // Don't render if no delivery mode selected or not a scheduled mode
    if (!delivery || !['DAILY', 'WEEKLY', 'SCHEDULE_ONCE'].includes(delivery)) {
        return null;
    }

    return (
        <div className="schedule-configuration">
            {delivery === 'DAILY' && (
                <div className="schedule-config daily-config">
                    <h4 className="config-title">📅 Daily Schedule Configuration</h4>
                    
                    <div className="form-row">
                        <div className="form-field">
                            <label htmlFor="daily-start-date">Start From (Optional)</label>
                            <Calendar
                                id="daily-start-date"
                                value={parseDateString(config.startDate)}
                                onChange={(e) => handleChange('startDate', formatDate(e.value))}
                                minDate={new Date()}
                                showIcon
                                dateFormat="M dd, yy"
                                placeholder="Leave empty to start immediately"
                                className={errors.startDate ? 'p-invalid' : ''}
                                showButtonBar
                                clearable
                                disabled={disabled}
                            />
                            {errors.startDate && <small className="p-error">{errors.startDate}</small>}
                            {!errors.startDate && <small className="p-help">Leave empty to start from today</small>}
                        </div>
                        
                        <div className="form-field">
                            <label htmlFor="daily-time">Time *</label>
                            <Calendar
                                id="daily-time"
                                value={parseTimeToDate(config.time)}
                                onChange={(e) => handleChange('time', formatTime(e.value))}
                                timeOnly
                                hourFormat="12"
                                showIcon
                                icon="pi pi-clock"
                                placeholder="Select time"
                                className={errors.time ? 'p-invalid' : ''}
                                disabled={disabled}
                            />
                            {errors.time && <small className="p-error">{errors.time}</small>}
                        </div>
                    </div>
                </div>
            )}

            {delivery === 'WEEKLY' && (
                <div className="schedule-config weekly-config">
                    <h4 className="config-title">📅 Weekly Schedule Configuration</h4>
                    
                    <div className="form-row">
                        <div className="form-field">
                            <label htmlFor="weekly-start-date">Start From (Optional)</label>
                            <Calendar
                                id="weekly-start-date"
                                value={parseDateString(config.startDate)}
                                onChange={(e) => handleChange('startDate', formatDate(e.value))}
                                minDate={new Date()}
                                showIcon
                                dateFormat="M dd, yy"
                                placeholder="Leave empty to start immediately"
                                className={errors.startDate ? 'p-invalid' : ''}
                                showButtonBar
                                clearable
                                disabled={disabled}
                            />
                            {errors.startDate && <small className="p-error">{errors.startDate}</small>}
                            {!errors.startDate && <small className="p-help">Leave empty to start from this week</small>}
                        </div>
                        
                        <div className="form-field">
                            <label htmlFor="weekly-day">Day of Week *</label>
                            <Dropdown
                                id="weekly-day"
                                value={config.dayOfWeek}
                                options={DAY_OPTIONS}
                                onChange={(e) => handleChange('dayOfWeek', e.value)}
                                placeholder="Select day"
                                className={errors.dayOfWeek ? 'p-invalid' : ''}
                                disabled={disabled}
                            />
                            {errors.dayOfWeek && <small className="p-error">{errors.dayOfWeek}</small>}
                        </div>
                        
                        <div className="form-field">
                            <label htmlFor="weekly-time">Time *</label>
                            <Calendar
                                id="weekly-time"
                                value={parseTimeToDate(config.time)}
                                onChange={(e) => handleChange('time', formatTime(e.value))}
                                timeOnly
                                hourFormat="12"
                                showIcon
                                icon="pi pi-clock"
                                placeholder="Select time"
                                className={errors.time ? 'p-invalid' : ''}
                                disabled={disabled}
                            />
                            {errors.time && <small className="p-error">{errors.time}</small>}
                        </div>
                    </div>
                </div>
            )}

            {delivery === 'SCHEDULE_ONCE' && (
                <div className="schedule-config schedule-once-config">
                    <h4 className="config-title">📅 One-Time Schedule Configuration</h4>
                    
                    <div className="form-row">
                        <div className="form-field">
                            <label htmlFor="once-date">Date *</label>
                            <Calendar
                                id="once-date"
                                value={parseDateString(config.date)}
                                onChange={(e) => handleChange('date', formatDate(e.value))}
                                minDate={new Date()}
                                showIcon
                                dateFormat="M dd, yy"
                                placeholder="Select date"
                                className={errors.date ? 'p-invalid' : ''}
                                disabled={disabled}
                            />
                            {errors.date && <small className="p-error">{errors.date}</small>}
                        </div>
                        
                        <div className="form-field">
                            <label htmlFor="once-time">Time *</label>
                            <Calendar
                                id="once-time"
                                value={parseTimeToDate(config.time)}
                                onChange={(e) => handleChange('time', formatTime(e.value))}
                                timeOnly
                                hourFormat="12"
                                showIcon
                                icon="pi pi-clock"
                                placeholder="Select time"
                                className={errors.time ? 'p-invalid' : ''}
                                disabled={disabled}
                            />
                            {errors.time && <small className="p-error">{errors.time}</small>}
                        </div>
                    </div>
                </div>
            )}

            {/* Days Ahead to Check field - only for EVENT workflow */}
            {workflowType === 'EVENT' && (
                <div className="schedule-config days-ahead-config" style={{ marginTop: '20px', padding: '15px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#f9f9f9' }}>
                    <h4 className="config-title">🔍 Notification Window Configuration</h4>
                    
                    <div className="form-row">
                        <div className="form-field">
                            <label htmlFor="days-ahead">Days Ahead to Check *</label>
                            <InputNumber
                                id="days-ahead"
                                value={config.daysAheadToCheck || 7}
                                onValueChange={(e) => handleChange('daysAheadToCheck', e.value)}
                                min={1}
                                max={365}
                                showButtons
                                buttonLayout="horizontal"
                                decrementButtonClassName="p-button-secondary"
                                incrementButtonClassName="p-button-secondary"
                                incrementButtonIcon="pi pi-plus"
                                decrementButtonIcon="pi pi-minus"
                                suffix=" days"
                                placeholder="Enter days"
                                disabled={disabled}
                                className="w-full"
                            />
                            <small className="p-help">
                                System will scan for events in the next {config.daysAheadToCheck || 7} days and send notifications for all events found in that window.
                            </small>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScheduleConfiguration;
